import { useDeviceDetector } from '@shared/hooks/useDeviceDetector';
import { slidePageMotion } from '@shared/motions/animations';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { hideNavigator, selectContentHeight, showNavigator } from '@store/reducers/appSlice';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { type FC, type PropsWithChildren, type ReactNode, useEffect, useState } from 'react';

import MobileBottomNavigation from '../Navigation/MobileBottomNavigation';
import MobileTopNavigation from '../Navigation/MobileTopNavigation';

interface AppContainerProps extends PropsWithChildren {
  fullScreen?: boolean;
  title?: ReactNode;
  subTitle?: string;
  backTitle?: ReactNode;
  controls?: ReactNode;
  singlePage?: boolean;
  noGap?: boolean;
  noTitleBorder?: boolean;
  background?: boolean;
  onBack?: () => void;
}

export const ResponsiveContainer: FC<AppContainerProps> = ({
  children,
  fullScreen,
  title,
  subTitle,
  backTitle,
  controls,
  singlePage,
  noGap,
  noTitleBorder = false,
  background,
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const deviceDetector = useDeviceDetector();
  const contentHeight = useAppSelector(selectContentHeight);

  const [displayNavigator, setDisplayNavigator] = useState(true);

  useEffect(() => {
    if (singlePage) {
      dispatch(hideNavigator());
      setDisplayNavigator(false);
    } else {
      dispatch(showNavigator());
      setDisplayNavigator(true);
    }

    return () => {
      dispatch(showNavigator());
    };
  }, [singlePage, dispatch]);

  return (
    <motion.div
      className={classNames('flex flex-col w-full relative', {
        'h-full': !fullScreen,
        'h-screen xl:h-full': fullScreen,
        'bg-base-100 dark:bg-base-200': !deviceDetector.isBigScreen && background,
      })}
      style={
        deviceDetector.isBigScreen ? { height: contentHeight, maxHeight: contentHeight } : undefined
      }
      {...slidePageMotion}
    >
      <div className="flex flex-col w-full h-full flex-1 justify-start items-center 2xl:py-4">
        <div
          className={classNames(
            'relative flex flex-col flex-1 w-full 2xl:max-w-5xl 2xl:w-5xl justify-start items-center 2xl:bg-base-200 dark:2xl:bg-base-300 2xl:rounded-2xl overflow-x-hidden 2xl:shadow-lg 2xl:outline 2xl:outline-base-100',
            {
              'max-h-screen h-screen 2xl:overflow-y-auto': !deviceDetector.isBigScreen,
              'h-full': deviceDetector.isBigScreen,
            },
          )}
        >
          {/* Content area that takes remaining height on big screens */}
          <div
            className={classNames('w-full h-full flex-1 relative', {
              'flex flex-col overflow-y-auto overflow-x-hidden': deviceDetector.isBigScreen,
              'flex flex-col': !deviceDetector.isBigScreen,
              'gap-0': noGap,
              'gap-6': !noGap,
            })}
          >
            {(title || controls) && (
              <div className="flex items-center w-full px-4 pt-3">
                {title && (
                  <div className="flex-col">
                    <h1 className="text-4xl font-extrabold">{title}</h1>
                    {subTitle && (
                      <h2 className="text-xs font-semibold text-base-content/35 shadow tracking-wide">
                        {subTitle}
                      </h2>
                    )}
                  </div>
                )}

                {controls && <div className="ml-auto">{controls}</div>}
              </div>
            )}

            <MobileTopNavigation onBack={onBack} backTitle={backTitle} noBorder={noTitleBorder} />

            {children}
          </div>

          {/* Tablet and Mobile Navigation */}
          {deviceDetector.isBigScreen && displayNavigator && (
            <MobileBottomNavigation showLabelOnActive />
          )}
        </div>
      </div>
    </motion.div>
  );
};
